import { getAcademicTerms } from '../services/calendarService';
import type { ProgramProgressCell } from '../types/domain';
import { loadAnnualProgressDataCache } from './annualProgressDataCache';
import { loadProgramProgressCellsCache, filterProgramProgressCellsByMondayIsoInclusive } from './programProgressCellsCache';
import { loadProgramFixedContentRowsCache } from './programFixedContentRowsCache';
import { parseISODate } from './date';

/**
 * 3~2월을 한 학년도로 보는 흔한 기준(3~12월 = 해당 연도, 1~2월 = 직전 연도).
 */
export function inferKoreanSchoolAcademicYear(d: Date): number {
  const m = d.getMonth() + 1;
  const y = d.getFullYear();
  if (m >= 3) return y;
  return y - 1;
}

/** MainLayout과 유사: 월 8~12, 1~7 를 2학기/1학기 휴리스틱 (로컬 달) */
export function heuristicTermNumberFromMonth(d: Date): 1 | 2 {
  const m = d.getMonth() + 1;
  return m >= 8 ? 2 : 1;
}

type TermPair = { academicYear: number; termNumber: 1 | 2 };

/**
 * 연간진도표와 동일: 학사일정 학년도 + `academic_terms`에서 `dateIso`가 속한 학기.
 * (연간진도표 1·2학기 탭과 같이, 그 날짜가 실제로 들어 있는 학기를 고른다)
 */
export function resolveAnnualProgressKeysForDate(
  workspaceId: string,
  dateIso: string,
  programSidebarAcademicYear: number | null
): TermPair {
  const parsed = parseISODate((dateIso ?? '').trim());
  const fallbackYear = !Number.isNaN(parsed.getTime())
    ? inferKoreanSchoolAcademicYear(parsed)
    : inferKoreanSchoolAcademicYear(new Date());

  const yearCandidates = Array.from(
    new Set(
      [programSidebarAcademicYear, fallbackYear, fallbackYear - 1, fallbackYear + 1].filter(
        (y): y is number => y != null && Number.isFinite(y)
      )
    )
  );

  if (workspaceId?.trim()) {
    for (const academicYear of yearCandidates) {
      for (const termNumber of [1, 2] as const) {
        const ap = loadAnnualProgressDataCache(workspaceId, academicYear, termNumber);
        if (!ap.found) continue;
        const termDef = ap.state.terms.find((t) => t.term_number === termNumber) ?? null;
        const startsOn = (termDef?.starts_on ?? '').trim();
        const endsOn = (termDef?.ends_on ?? '').trim();
        if (!startsOn || !endsOn) continue;
        if (dateIso >= startsOn && dateIso <= endsOn) {
          return { academicYear, termNumber };
        }
      }
    }
  }

  const academicYear = programSidebarAcademicYear ?? fallbackYear;
  const d = !Number.isNaN(parsed.getTime()) ? parsed : new Date();
  return { academicYear, termNumber: heuristicTermNumberFromMonth(d) };
}

function annualCellKeyPrefix(gradeId: string, mondayIso: string, weekdayKey: string): string {
  return `${gradeId}|${mondayIso}-${weekdayKey}-`;
}

/**
 * `academic_terms` 테이블(및 캐시)에서 오늘 날짜가 들어가는 학기.
 * 학년도 후보: 달력 연도 기준 y-2 ~ y+2.
 */
export async function resolveTodaySchoolTerm(
  workspaceId: string,
  todayIso: string
): Promise<TermPair | null> {
  const y = new Date().getFullYear();
  for (const ay of [y + 1, y, y - 1, y - 2, y - 3]) {
    try {
      const terms = await getAcademicTerms(workspaceId, ay);
      for (const t of terms) {
        if (t.term_number !== 1 && t.term_number !== 2) continue;
        if (todayIso >= t.starts_on && todayIso <= t.ends_on) {
          return { academicYear: t.academic_year, termNumber: t.term_number };
        }
      }
    } catch {
      /* try next */
    }
  }
  return null;
}

/**
 * 연간진도표 등에서 이미 채운 program_progress 캐시 키를 스캔해,
 * 오늘(월요일·weekday)에 맞는 행이 있으면 그때 쓰인 학년도·학기.
 */
export function findTermFromProgramProgressCache(
  workspaceId: string,
  gradeId: string,
  mondayIso: string,
  weekdayKey: string
): TermPair | null {
  const anchor = (() => {
    const p = parseISODate((mondayIso ?? '').trim());
    return !Number.isNaN(p.getTime()) ? p : new Date();
  })();
  const base = inferKoreanSchoolAcademicYear(anchor);
  for (const ay of [base - 1, base, base + 1, base - 2, base + 2]) {
    for (const termNumber of [1, 2] as const) {
      const { found, rows } = loadProgramProgressCellsCache(workspaceId, ay, termNumber, gradeId);
      if (!found || !rows.length) continue;
      if (
        rows.some(
          (r) => (r.monday_iso ?? '').trim() === mondayIso && (r.weekday_key ?? '') === weekdayKey
        )
      ) {
        return { academicYear: ay, termNumber };
      }
    }
  }
  return null;
}

/**
 * 1) academic_terms
 * 2) program_progress LS 캐시(연간진도·프로그램에서 로드한 데이터)
 * 3) 한국어 학년도 + 월 휴리스틱
 */
export async function resolveTermForDashboard(
  workspaceId: string,
  todayIso: string,
  gradeId: string,
  mondayIso: string,
  weekdayKey: string,
  programSidebarAcademicYear: number | null
): Promise<TermPair> {
  if (programSidebarAcademicYear != null) {
    try {
      const terms = await getAcademicTerms(workspaceId, programSidebarAcademicYear);
      for (const t of terms) {
        if (t.term_number !== 1 && t.term_number !== 2) continue;
        if (todayIso >= t.starts_on && todayIso <= t.ends_on) {
          return { academicYear: t.academic_year, termNumber: t.term_number };
        }
      }
    } catch {
      /* fall through */
    }
  }

  const fromTable = await resolveTodaySchoolTerm(workspaceId, todayIso);
  if (fromTable) return fromTable;

  const fromCache = findTermFromProgramProgressCache(workspaceId, gradeId, mondayIso, weekdayKey);
  if (fromCache) return fromCache;

  const d = (() => {
    const p = parseISODate(todayIso);
    return !Number.isNaN(p.getTime()) ? p : new Date();
  })();
  return {
    academicYear: inferKoreanSchoolAcademicYear(d),
    termNumber: heuristicTermNumberFromMonth(d),
  };
}

/**
 * 대시보드: 연간진도표와 같은 학년도·학기 키를 쓰고, 해당 주·요일 연간셀이 다른 학기 캐시에만
 * 있으면 그 학기로 보정한다(고정 프로그램은 program_progress 셀이 없을 수 있음).
 */
export function pickDashboardProgressTerm(
  workspaceId: string,
  gradeId: string,
  mondayIso: string,
  weekdayKey: string | null,
  todayIso: string,
  programSidebarAcademicYear: number | null
): TermPair {
  void gradeId;
  void mondayIso;
  void weekdayKey;
  return resolveAnnualProgressKeysForDate(workspaceId, todayIso, programSidebarAcademicYear);
}

/**
 * 대시보드: `program_progress` 셀을 Supabase에 요청하지 않고, 연간진도·프로그램이 쓰는 LS 캐시에서만
 * 보는 날짜·주(`mondayIso`+`weekdayKey`)에 맞는 `slot_index → 셀`을 만든다. 추정이 빗나가면
 * `findTermFromProgramProgressCache`와 동일한 학년도 루프·1·2학기를 끝까지 스캔한다.
 */
export function getDashboardProgramProgressSlotMapFromLocalCache(
  workspaceId: string,
  gradeId: string,
  mondayIso: string,
  weekdayKey: string,
  todayIso: string,
  programSidebarAcademicYear: number | null
): Map<number, ProgramProgressCell> {
  const buildMap = (academicYear: number, termNumber: 1 | 2): Map<number, ProgramProgressCell> => {
    const { found, rows } = loadProgramProgressCellsCache(workspaceId, academicYear, termNumber, gradeId);
    if (!found) return new Map();
    const week = filterProgramProgressCellsByMondayIsoInclusive(rows, mondayIso, mondayIso);
    const day = week.filter((r) => (r.weekday_key ?? '') === weekdayKey);
    const m = new Map<number, ProgramProgressCell>();
    day.forEach((c) => m.set(c.slot_index, c));
    return m;
  };

  const tryPair = (ay: number, tn: 1 | 2): Map<number, ProgramProgressCell> => {
    let m = buildMap(ay, tn);
    if (m.size === 0) m = buildMap(ay, tn === 1 ? 2 : 1);
    return m;
  };

  const fromScan = findTermFromProgramProgressCache(workspaceId, gradeId, mondayIso, weekdayKey);
  if (fromScan) {
    const m = tryPair(fromScan.academicYear, fromScan.termNumber);
    if (m.size > 0) return m;
  }

  const term = pickDashboardProgressTerm(
    workspaceId,
    gradeId,
    mondayIso,
    weekdayKey,
    todayIso,
    programSidebarAcademicYear
  );
  let m = tryPair(term.academicYear, term.termNumber);
  if (m.size > 0) return m;

  const anchor = (() => {
    const p = parseISODate((mondayIso ?? '').trim());
    return !Number.isNaN(p.getTime()) ? p : new Date();
  })();
  const base = inferKoreanSchoolAcademicYear(anchor);
  for (const ay of [base - 1, base, base + 1, base - 2, base + 2]) {
    const got = tryPair(ay, 1);
    if (got.size > 0) return got;
  }
  return new Map();
}
